import type { CanonicalPrivateEvent, PrivateEventName, SourcePolicy } from '@/lib/realtime/contracts';
import { TtlMap } from './ttl-map';

/** Which transport delivered an event. */
export type Source = 'backend' | 'direct';

/** A canonical event arrival, tagged with its transport source. */
export interface IngestedEvent<E extends PrivateEventName = PrivateEventName> {
  name: E;
  payload: CanonicalPrivateEvent<E>;
  source: Source;
  plaeeEventId: string;
}

type AnyHandler = (e: IngestedEvent) => void;

const DEFAULT_DEDUP_TTL_MS = 30_000;

/**
 * Client-side bus that ingests private events from both transports (backend
 * Socket.IO `user:<id>` room + direct Plaee Centrifugo channel) and applies a
 * per-event {@link SourcePolicy} to pick a winner.
 *
 * Feature code never subscribes to a transport directly — it consumes the bus
 * via {@link useRealtimePrivateEvent}. See realtime-architecture.md
 * "Private channels — dual-path design".
 */
export class PrivateEventBus {
  private readonly handlers = new Map<PrivateEventName, Set<AnyHandler>>();
  private readonly policies = new Map<PrivateEventName, SourcePolicy>();
  private readonly dedup = new TtlMap<string, true>();
  private readonly pendingFallback = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(initialPolicies: Record<PrivateEventName, SourcePolicy>) {
    for (const [name, policy] of Object.entries(initialPolicies) as Array<[PrivateEventName, SourcePolicy]>) {
      this.policies.set(name, policy);
    }
  }

  ingest(event: IngestedEvent): void {
    const policy = this.policies.get(event.name) ?? { mode: 'both', dedupTtlMs: DEFAULT_DEDUP_TTL_MS };
    switch (policy.mode) {
      case 'backend-only':
        if (event.source === 'backend') this.dispatch(event);
        return;
      case 'direct-only':
        if (event.source === 'direct') this.dispatch(event);
        return;
      case 'prefer-direct':
        this.preferOne(event, 'direct', policy.fallbackMs);
        return;
      case 'prefer-backend':
        this.preferOne(event, 'backend', policy.fallbackMs);
        return;
      case 'both':
        if (this.dedup.has(event.plaeeEventId)) return;
        this.dedup.set(event.plaeeEventId, true, policy.dedupTtlMs);
        this.dispatch(event);
        return;
    }
  }

  setPolicy(name: PrivateEventName, policy: SourcePolicy): void {
    this.policies.set(name, policy);
  }

  on<E extends PrivateEventName>(name: E, handler: (e: IngestedEvent<E>) => void): () => void {
    const set = this.handlers.get(name) ?? new Set<AnyHandler>();
    set.add(handler as AnyHandler);
    this.handlers.set(name, set);
    return () => {
      set.delete(handler as AnyHandler);
    };
  }

  private dispatch(event: IngestedEvent): void {
    const set = this.handlers.get(event.name);
    if (!set) return;
    for (const h of set) h(event);
  }

  /**
   * prefer-X: if the preferred source arrives first, dispatch and arm dedup.
   * If the non-preferred arrives first, hold it for `fallbackMs` then release.
   */
  private preferOne(event: IngestedEvent, preferred: Source, fallbackMs: number): void {
    if (event.source === preferred) {
      if (this.dedup.has(event.plaeeEventId)) return;
      this.dedup.set(event.plaeeEventId, true, DEFAULT_DEDUP_TTL_MS);
      const pending = this.pendingFallback.get(event.plaeeEventId);
      if (pending !== undefined) {
        clearTimeout(pending);
        this.pendingFallback.delete(event.plaeeEventId);
      }
      this.dispatch(event);
      return;
    }
    if (this.dedup.has(event.plaeeEventId)) return;
    const timer = setTimeout(() => {
      this.pendingFallback.delete(event.plaeeEventId);
      if (this.dedup.has(event.plaeeEventId)) return;
      this.dedup.set(event.plaeeEventId, true, DEFAULT_DEDUP_TTL_MS);
      this.dispatch(event);
    }, fallbackMs);
    this.pendingFallback.set(event.plaeeEventId, timer);
  }
}
